import { z } from "zod";

/** Obstetrics / pregnancy tracking contracts + the deterministic dating math. */

export const pregnancyStatusSchema = z.enum([
	"ongoing",
	"delivered",
	"miscarried",
	"terminated",
]);
export type PregnancyStatus = z.infer<typeof pregnancyStatusSchema>;

// Dates are 'YYYY-MM-DD' (Postgres `date`).
const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const pregnancyApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	lmp_date: dateStr.nullable(),
	edd_override: dateStr.nullable(),
	gravida: z.number().int().nullable(),
	para: z.number().int().nullable(),
	/** Booking weight + height → pre-pregnancy BMI → recommended weight gain. */
	pre_pregnancy_weight_kg: z.number().nullable(),
	height_cm: z.number().nullable(),
	status: pregnancyStatusSchema,
	outcome_date: dateStr.nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type PregnancyApi = z.infer<typeof pregnancyApiSchema>;

export const createPregnancyInputSchema = z
	.object({
		patient_id: z.string().uuid(),
		lmp_date: dateStr.nullable().optional(),
		edd_override: dateStr.nullable().optional(),
		gravida: z.number().int().min(0).max(30).nullable().optional(),
		para: z.number().int().min(0).max(30).nullable().optional(),
		pre_pregnancy_weight_kg: z.coerce
			.number()
			.positive()
			.max(400)
			.nullable()
			.optional(),
		height_cm: z.coerce.number().positive().max(250).nullable().optional(),
		notes: z.string().max(1000).optional(),
	})
	.refine((v) => Boolean(v.lmp_date || v.edd_override), {
		message: "Either LMP date or an ultrasound EDD is required",
	});
export type CreatePregnancyInput = z.infer<typeof createPregnancyInputSchema>;

export const updatePregnancyInputSchema = z.object({
	lmp_date: dateStr.nullable().optional(),
	edd_override: dateStr.nullable().optional(),
	gravida: z.number().int().min(0).max(30).nullable().optional(),
	para: z.number().int().min(0).max(30).nullable().optional(),
	pre_pregnancy_weight_kg: z.coerce
		.number()
		.positive()
		.max(400)
		.nullable()
		.optional(),
	height_cm: z.coerce.number().positive().max(250).nullable().optional(),
	status: pregnancyStatusSchema.optional(),
	outcome_date: dateStr.nullable().optional(),
	notes: z.string().max(1000).nullable().optional(),
});
export type UpdatePregnancyInput = z.infer<typeof updatePregnancyInputSchema>;

const GESTATION_DAYS = 280; // Naegele: EDD = LMP + 280 days (40 weeks)
const MS_PER_DAY = 86_400_000;

/** Parse a 'YYYY-MM-DD' as a UTC midnight timestamp (no TZ drift). */
function parseDateUtc(s: string): number {
	const [y, m, d] = s.split("-").map(Number);
	return Date.UTC(y!, (m ?? 1) - 1, d ?? 1);
}

function todayUtc(asOf: Date): number {
	// Gestational age is a CALENDAR-day count, so "today" must be the day on the
	// clinic's wall clock. In the browser (the live-render path) the local date
	// components ARE the clinic's local date — using getUTCDate() instead would
	// lag by the timezone offset and shift GA a day late at night east of UTC
	// (e.g. 00:00–03:00 Baghdad reads as the previous UTC day). Local components
	// fix that. (Server callers run in UTC; GA there is informational only.)
	return Date.UTC(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
}

function toIsoDate(ms: number): string {
	return new Date(ms).toISOString().slice(0, 10);
}

export interface PregnancyDating {
	/** Estimated due date, 'YYYY-MM-DD'. */
	edd: string;
	/** Gestational age in whole days (can be <0 pre-conception or >280 post-term). */
	gaDays: number;
	gaWeeks: number;
	gaDaysRemainder: number;
	/** 1 | 2 | 3, or null when GA is out of a sensible range. */
	trimester: 1 | 2 | 3 | null;
	/** 0–100, clamped, for a 40-week progress bar. */
	progressPct: number;
}

/**
 * Compute live dating from the stored inputs. Ultrasound EDD is authoritative
 * when present; otherwise EDD = LMP + 280d. GA is measured against EDD so the
 * two are always consistent. Pure — depends only on its args (+ `asOf`).
 */
export function computePregnancy(
	lmpDate: string | null | undefined,
	eddOverride: string | null | undefined,
	asOf: Date = new Date(),
): PregnancyDating | null {
	let eddMs: number;
	if (eddOverride) {
		eddMs = parseDateUtc(eddOverride);
	} else if (lmpDate) {
		eddMs = parseDateUtc(lmpDate) + GESTATION_DAYS * MS_PER_DAY;
	} else {
		return null;
	}

	const today = todayUtc(asOf);
	const gaDays = Math.round(GESTATION_DAYS - (eddMs - today) / MS_PER_DAY);
	const gaWeeks = Math.floor(gaDays / 7);
	const gaDaysRemainder = ((gaDays % 7) + 7) % 7;

	let trimester: 1 | 2 | 3 | null = null;
	if (gaDays < 0)
		trimester = null; // pre-conception (future LMP — data-entry error)
	else if (gaDays < 98)
		trimester = 1; // 0w–13w6d
	else if (gaDays < 196)
		trimester = 2; // 14w0d–27w6d
	else if (gaDays <= 308) trimester = 3; // 28w0d–44w (allow post-term)

	const progressPct = Math.max(
		0,
		Math.min(100, (gaDays / GESTATION_DAYS) * 100),
	);

	return {
		edd: toIsoDate(eddMs),
		gaDays,
		gaWeeks,
		gaDaysRemainder,
		trimester,
		progressPct,
	};
}

// ===== Obstetric ultrasound (structured scan results) =====
//
// Biometry + GA are READ OFF the ultrasound machine and entered by the
// sonographer/doctor (every scanner computes them). The system computes only
// DATE arithmetic from them — EDD-by-ultrasound and the LMP↔US reconciliation
// — so there is zero medical-formula risk; the dating thresholds are ACOG
// Committee Opinion 700, applied transparently (recommend, never auto-redate).

export const obstetricUltrasoundApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	pregnancy_id: z.string().uuid(),
	scan_date: dateStr,
	/** GA by ultrasound as measured/printed by the scanner. */
	ga_weeks: z.number().int().nullable(),
	ga_days: z.number().int().nullable(),
	/** Estimated fetal weight (grams) — entered from the machine. */
	efw_grams: z.number().int().nullable(),
	bpd_mm: z.number().nullable(),
	hc_mm: z.number().nullable(),
	ac_mm: z.number().nullable(),
	fl_mm: z.number().nullable(),
	crl_mm: z.number().nullable(),
	fhr_bpm: z.number().int().nullable(),
	afi_cm: z.number().nullable(),
	placenta: z.string().nullable(),
	presentation: z.string().nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type ObstetricUltrasoundApi = z.infer<
	typeof obstetricUltrasoundApiSchema
>;

export const createObstetricUltrasoundInputSchema = z.object({
	pregnancy_id: z.string().uuid(),
	scan_date: dateStr,
	ga_weeks: z.coerce.number().int().min(0).max(45).nullable().optional(),
	ga_days: z.coerce.number().int().min(0).max(6).nullable().optional(),
	efw_grams: z.coerce.number().int().min(1).max(9000).nullable().optional(),
	bpd_mm: z.coerce.number().positive().max(200).nullable().optional(),
	hc_mm: z.coerce.number().positive().max(500).nullable().optional(),
	ac_mm: z.coerce.number().positive().max(600).nullable().optional(),
	fl_mm: z.coerce.number().positive().max(120).nullable().optional(),
	crl_mm: z.coerce.number().positive().max(120).nullable().optional(),
	fhr_bpm: z.coerce.number().int().min(60).max(240).nullable().optional(),
	afi_cm: z.coerce.number().min(0).max(50).nullable().optional(),
	placenta: z.string().max(200).nullable().optional(),
	presentation: z.string().max(80).nullable().optional(),
	notes: z.string().max(1000).nullable().optional(),
});
export type CreateObstetricUltrasoundInput = z.infer<
	typeof createObstetricUltrasoundInputSchema
>;

/** EDD implied by an ultrasound's GA at the scan date (pure date math). */
export function eddFromUltrasound(
	scanDate: string,
	gaWeeks: number | null | undefined,
	gaDays: number | null | undefined,
): string | null {
	if (gaWeeks == null && gaDays == null) return null;
	const gaAtScan = (gaWeeks ?? 0) * 7 + (gaDays ?? 0);
	const eddMs =
		parseDateUtc(scanDate) + (GESTATION_DAYS - gaAtScan) * MS_PER_DAY;
	return toIsoDate(eddMs);
}

/**
 * ACOG (Committee Opinion 700) maximum LMP-vs-US discrepancy before ultrasound
 * dating should be preferred, keyed by GA-at-scan. Returns the threshold in
 * days for the window the scan falls in.
 */
export function redatingThresholdDays(gaDaysAtScan: number): number {
	if (gaDaysAtScan <= 62) return 5; // ≤ 8w6d (CRL)
	if (gaDaysAtScan <= 97) return 7; // 9w0d–13w6d
	if (gaDaysAtScan <= 111) return 7; // 14w0d–15w6d
	if (gaDaysAtScan <= 153) return 10; // 16w0d–21w6d
	if (gaDaysAtScan <= 195) return 14; // 22w0d–27w6d
	return 21; // ≥ 28w0d
}

export interface UltrasoundDatingReconciliation {
	edd_lmp: string;
	edd_us: string;
	discrepancy_days: number;
	threshold_days: number;
	/** True when |LMP−US| exceeds the ACOG threshold → consider US dating. */
	should_redate: boolean;
}

/**
 * Compare the LMP-based EDD with the ultrasound EDD and apply the ACOG
 * redating threshold for the GA at the scan. Pure; recommends only.
 */
export function ultrasoundDatingReconciliation(
	eddLmp: string | null | undefined,
	scanDate: string,
	gaWeeks: number | null | undefined,
	gaDays: number | null | undefined,
): UltrasoundDatingReconciliation | null {
	if (!eddLmp) return null;
	const eddUs = eddFromUltrasound(scanDate, gaWeeks, gaDays);
	if (!eddUs) return null;
	const discrepancy = Math.round(
		Math.abs(parseDateUtc(eddLmp) - parseDateUtc(eddUs)) / MS_PER_DAY,
	);
	const gaAtScan = (gaWeeks ?? 0) * 7 + (gaDays ?? 0);
	const threshold = redatingThresholdDays(gaAtScan);
	return {
		edd_lmp: eddLmp,
		edd_us: eddUs,
		discrepancy_days: discrepancy,
		threshold_days: threshold,
		should_redate: discrepancy > threshold,
	};
}

// ===== Antenatal checkups (per-visit measurements) =====

export const urineProteinSchema = z.enum([
	"negative",
	"trace",
	"1+",
	"2+",
	"3+",
	"4+",
]);
export type UrineProtein = z.infer<typeof urineProteinSchema>;

export const antenatalCheckupApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	pregnancy_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	checked_at: z.string().datetime(),
	bp_systolic: z.number().int().nullable(),
	bp_diastolic: z.number().int().nullable(),
	weight_kg: z.number().nullable(),
	fundal_height_cm: z.number().nullable(),
	fetal_heart_rate: z.number().int().nullable(),
	urine_protein: urineProteinSchema.nullable(),
	edema: z.boolean().nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type AntenatalCheckupApi = z.infer<typeof antenatalCheckupApiSchema>;

export const createAntenatalCheckupInputSchema = z.object({
	pregnancy_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	checked_at: z.string().datetime().optional(),
	bp_systolic: z.coerce.number().int().min(40).max(300).nullable().optional(),
	bp_diastolic: z.coerce.number().int().min(20).max(200).nullable().optional(),
	weight_kg: z.coerce.number().positive().max(400).nullable().optional(),
	fundal_height_cm: z.coerce.number().positive().max(60).nullable().optional(),
	fetal_heart_rate: z.coerce
		.number()
		.int()
		.min(60)
		.max(240)
		.nullable()
		.optional(),
	urine_protein: urineProteinSchema.nullable().optional(),
	edema: z.boolean().nullable().optional(),
	notes: z.string().max(1000).optional(),
});
export type CreateAntenatalCheckupInput = z.infer<
	typeof createAntenatalCheckupInputSchema
>;

// ===== Clinical safety helpers (pure) =====

export type BpCategory = "normal" | "elevated" | "hypertension" | "severe";

/**
 * Blood-pressure category using the obstetric thresholds: ≥140/90 is
 * gestational hypertension, ≥160/110 is severe (needs urgent review).
 * Either value crossing a threshold sets the category (uses the worse one).
 */
export function assessBp(
	systolic: number | null | undefined,
	diastolic: number | null | undefined,
): BpCategory | null {
	if (systolic == null && diastolic == null) return null;
	const s = systolic ?? 0;
	const d = diastolic ?? 0;
	if (s >= 160 || d >= 110) return "severe";
	if (s >= 140 || d >= 90) return "hypertension";
	if (s >= 130 || d >= 80) return "elevated";
	return "normal";
}

/**
 * Pre-eclampsia concern: new hypertension + proteinuria, or severe-range BP
 * alone. Conservative — surfaces a banner urging review, never a diagnosis.
 */
export function isPreeclampsiaConcern(
	bp: BpCategory | null,
	protein: UrineProtein | null | undefined,
): boolean {
	if (bp === "severe") return true;
	const htn = bp === "hypertension";
	const proteinuria =
		protein === "1+" ||
		protein === "2+" ||
		protein === "3+" ||
		protein === "4+";
	return htn && proteinuria;
}

export type PrePregnancyBmiCategory =
	| "underweight"
	| "normal"
	| "overweight"
	| "obese";

export function prePregnancyBmi(
	weightKg: number | null | undefined,
	heightCm: number | null | undefined,
): number | null {
	if (!weightKg || !heightCm) return null;
	const m = heightCm / 100;
	if (m <= 0) return null;
	return Math.round((weightKg / (m * m)) * 10) / 10;
}

export function prePregnancyBmiCategory(
	bmi: number | null,
): PrePregnancyBmiCategory | null {
	if (bmi == null) return null;
	if (bmi < 18.5) return "underweight";
	if (bmi < 25) return "normal";
	if (bmi < 30) return "overweight";
	return "obese";
}

/** IOM (2009) total recommended gestational weight gain for a singleton, in kg. */
export function recommendedWeightGainKg(
	cat: PrePregnancyBmiCategory | null,
): { min: number; max: number } | null {
	switch (cat) {
		case "underweight":
			return { min: 12.5, max: 18 };
		case "normal":
			return { min: 11.5, max: 16 };
		case "overweight":
			return { min: 7, max: 11.5 };
		case "obese":
			return { min: 5, max: 9 };
		default:
			return null;
	}
}

export type MilestoneStatus = "past" | "current" | "upcoming";

export interface AntenatalMilestone {
	key: string;
	fromWeek: number;
	toWeek: number;
	status: MilestoneStatus;
}

const ANTENATAL_MILESTONES: Array<{
	key: string;
	fromWeek: number;
	toWeek: number;
}> = [
	{ key: "booking", fromWeek: 0, toWeek: 12 },
	{ key: "dating_scan", fromWeek: 8, toWeek: 14 },
	{ key: "nt_scan", fromWeek: 11, toWeek: 14 },
	{ key: "anomaly_scan", fromWeek: 18, toWeek: 22 },
	{ key: "ogtt", fromWeek: 24, toWeek: 28 },
	{ key: "tdap", fromWeek: 27, toWeek: 36 },
	{ key: "gbs_swab", fromWeek: 35, toWeek: 37 },
	{ key: "term", fromWeek: 37, toWeek: 40 },
];

/**
 * Recommended antenatal milestones with each marked past / current / upcoming
 * relative to the current gestational age. Pure guidance (not a done-tracker).
 */
export function antenatalSchedule(
	gaWeeks: number | null | undefined,
): AntenatalMilestone[] {
	return ANTENATAL_MILESTONES.map((m) => {
		let status: MilestoneStatus;
		if (gaWeeks == null) status = "upcoming";
		else if (gaWeeks > m.toWeek) status = "past";
		else if (gaWeeks >= m.fromWeek) status = "current";
		else status = "upcoming";
		return { ...m, status };
	});
}
